import type { JSX } from 'react';
import { Button, Divider, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import { BarChart3, Boxes, FolderKanban, type LucideIcon } from 'lucide-react';

import { Modal } from '~/components/Modal';

interface HelpSection {
  icon: LucideIcon;
  heading: string;
  body: string;
}

export interface HelpProductModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HELP_SECTIONS: HelpSection[] = [
  {
    icon: FolderKanban,
    heading: 'Projects are tied to one repository',
    body: "When Compounds works on a repository, it creates a project, a development spec for the change. Run enough of these across enough repos and it gets hard to track, especially when repo names aren't descriptive.",
  },
  {
    icon: Boxes,
    heading: 'Products group your repositories',
    body: "A product is a named group of repositories, say, everything behind your billing system. Every project in a linked repository shows up on the product. The same repository can belong to more than one product, so a shared library or internal service doesn't need to pick just one owner.",
  },
  {
    icon: BarChart3,
    heading: 'Grouping makes analytics possible',
    body: 'With repositories grouped into a product, Compounds rolls up analytics, like velocity, across the whole product instead of repo by repo.',
  },
];

/**
 * Static informational modal explaining what a "Product" is in Compounds.
 * Props-only, no data fetching — content never changes at runtime.
 *
 * Pattern: docs/design-patterns/frontend/component-patterns.md (display-components-props-only)
 */
export function HelpProductModal({ isOpen, onClose }: HelpProductModalProps): JSX.Element {
  return (
    <Modal isOpen={isOpen} maxWidth="xl" onOpenChange={onClose}>
      <ModalContent>
        <ModalHeader>What is a Product?</ModalHeader>
        <Divider />
        <ModalBody className="gap-4 text-sm text-foreground">
          <p>
            The more repositories Compounds is working across, the easier it is to lose track of
            what&apos;s happening where. Products group them together to fix that.
          </p>
          {HELP_SECTIONS.map(({ body, heading, icon: Icon }) => (
            <div key={heading} className="flex gap-3">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon className="h-4 w-4" />
              </div>
              <div>
                <p className="font-medium text-foreground">{heading}</p>
                <p className="mt-1 text-muted">{body}</p>
              </div>
            </div>
          ))}
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-foreground">
            A Product gives you an organized picture of what you&apos;re building, not just a flat
            list of repos.
          </div>
        </ModalBody>
        <Divider />
        <ModalFooter>
          <Button color="primary" onPress={onClose}>
            Got it
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
